import { getBusinesses } from "./api_calls/getBusinesses";
import { getCandleData } from "@/_core/data/api_calls/getCandleData";
import { getClients } from "./api_calls/getClients";
import { getEndProducts } from "./api_calls/getEndProducts";
import { getPlants } from "./api_calls/getPlant";
import { getProviders } from "./api_calls/getProvidert";
import { saveUserPreferences } from "@/_core/data/SaveToDB";

/**
 * Function used in Overview.vue to retrieve historical data.
 * @param {object} _this - Component context (typically 'this')
 * @param {number} chartNum - Chart identifier (1 to 4)
 * @param {number} selAssetType - Asset type: 0 = currency, 1 = commodity, 2 = index
 * @param {number} assetNumber - Primary key (ID) of the selected asset
 * @param {number} [commodityContract=1] - Contract month for commodity continuation (optional)
 */
export function getChartData(
  _this,
  chartNum,
  selAssetType,
  assetNumber,
  commodityContract = 1
) {
  if (chartNum === 1) {
    _this.chartDataRaw1 = [];
    _this.spot1 = null;
    _this.showLoadingAsset1 = true;
  } else if (chartNum === 2) {
    _this.chartDataRaw2 = [];
    _this.spot2 = null;
    _this.showLoadingAsset2 = true;
  } else if (chartNum === 3) {
    _this.chartDataRaw3 = [];
    _this.spot3 = null;
    _this.showLoadingAsset3 = true;
  } else if (chartNum === 4) {
    _this.chartDataRaw4 = [];
    _this.spot4 = null;
    _this.showLoadingAsset4 = true;
  }
  if (selAssetType === 0) {
    // 0: CURRENCY
    _this.currencyNumber = assetNumber;
    return getCandleData(_this, "technical_analysis_historical", {
      currencyNumber: assetNumber
    });
  } else if (selAssetType === 1) {
    // 1: COMMODITY
    return getCandleData(_this, "commodity", {
      commodityNumber: assetNumber,
      contract: commodityContract
    });
  } else if (selAssetType === 2) {
    // 3: INDEX
    return getCandleData(_this, "index", {
      index: assetNumber,
      initDate: _this.initCurrencyDate
    });
  }
}

/**
 * Converts an asset type string to its corresponding index.
 * @param {string} asset - Asset type as a string ("currency", "commodity", or "index")
 * @returns {number} - Corresponding index: 0 = currency, 1 = commodity, 2 = index; returns -1 if unrecognized
 */
export function assetType(asset) {
  return asset === "currency"
    ? 0
    : asset === "commodity"
    ? 1
    : asset === "index"
    ? 2
    : -1;
}

/**
 * Generates table headers by extracting keys from the first row of data,
 * excluding predefined ones.
 * @param {object} _this - Component context containing table data
 */
export function createTableHeaders(_this) {
  if (_this.tableData.length === 0) {
    _this.tableHeaders = ["spot"];
  } else {
    let keys = Object.keys(_this.tableData[0]);
    _this.tableHeaders = keys.filter(
      d =>
        d !== "row" &&
        d !== "contract" &&
        d !== "close" &&
        d !== "asset" &&
        d !== "change" &&
        d !== "show" &&
        d !== "previous_value" &&
        d !== "render" &&
        d !== "type"
    );
  }
}

/**
 * If the client doesn't have a view, this is the default
 */
export function defaultViewSelection() {
  return [
    "EURUSD",
    "GBPUSD",
    "USDBRL",
    "USDCOP",
    "USDMXN",
    "USDMYR",
    "BRENT",
    "CORN",
    "CPO CIF ROTT",
    "CPO MAL MYR",
    "CPO MAL USD",
    "PKO",
    "SOYBEAN",
    "SOYBEAN OIL",
    "SUGAR",
    "WTI",
    "DXY",
    "NASDAQ",
    "S&P500",
    "VIX"
  ];
}
/**
 * If the client doesn't have a view, this are the default preferences
 */
export function getDefaultOverviewPreferences(_this) {
  if (
    Object.keys(_this.$store.state.user_preferences.all.items.overview_choices)
      .length === 0
  ) {
    let choices = {
      color_theme: false,
      selected_values: [
        "EURUSD",
        "GBPUSD",
        "USDBRL",
        "USDCOP",
        "USDMXN",
        "USDMYR",
        "BRENT",
        "CORN",
        "CPO CIF ROTT",
        "CPO MAL MYR",
        "CPO MAL USD",
        "PKO",
        "SOYBEAN",
        "SOYBEAN OIL",
        "SUGAR",
        "WTI",
        "DXY",
        "NASDAQ",
        "S&P500",
        "VIX"
      ]
    };
    _this.$store.state.user_preferences.all.items.overview_choices = choices;
    saveUserPreferences(_this);
    return { choices };
  } else {
    let choices =
      _this.$store.state.user_preferences.all.items.overview_choices;
    return { choices };
  }
}

/**
 * Sets default hedge preferences if none are defined by the client.
 * @param {object} _this - Component context (typically 'this')
 * @returns {object} choices - The hedge preference settings
 */
export function getDefaultHedgePreferences(_this) {
  const hedgePrefs =
    _this.$store.state.user_preferences.all.items.hedges_choices;

  if (Object.keys(hedgePrefs).length === 0) {
    const choices = { color_theme: true };
    _this.$store.state.user_preferences.all.items.hedges_choices = choices;
    saveUserPreferences(_this);
    return { choices };
  } else {
    return { choices: hedgePrefs };
  }
}

/**
 * get companyParameters
 * @param { object } companies initial company information
 */
export async function getCompanyParameters(companies) {
  let companyParameters = {
    businesses: [],
    clients: [],
    endProducts: [],
    plants: [],
    providers: [],
    ready: false
  };
  await getEndProducts(companies)
    .then(data => {
      data = JSON.parse(JSON.stringify(data));
      Object.assign(companyParameters, { endProducts: data });
    })
    .catch(function(error) {
      console.log(error);
    });
  await getPlants(companies)
    .then(data => {
      data = JSON.parse(JSON.stringify(data));
      Object.assign(companyParameters, { plants: data });
    })
    .catch(function(error) {
      console.log(error);
    });
  await getProviders(companies)
    .then(data => {
      data = JSON.parse(JSON.stringify(data));
      Object.assign(companyParameters, { providers: data });
    })
    .catch(function(error) {
      console.log(error);
    });
  await getClients(companies)
    .then(data => {
      data = JSON.parse(JSON.stringify(data));
      Object.assign(companyParameters, { clients: data });
    })
    .catch(function(error) {
      console.log(error);
    });
  await getBusinesses(companies)
    .then(data => {
      data = JSON.parse(JSON.stringify(data));
      Object.assign(companyParameters, { businesses: data });
    })
    .catch(function(error) {
      console.log(error);
    });
  Object.assign(companyParameters, { ready: true });
  return companyParameters;
}
